import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "../ui/sonner";
import { authStore } from "../../lib/api";
import { CheckSquare, ExternalLink, FileSignature, History, Loader2, Maximize2, Minimize2, RefreshCw, RotateCcw, Save, ShieldCheck, Square, Wand2 } from "lucide-react";
import { DocxPlanEditor, planLocked, rowsFromComputed, templateRows } from "./DocxPlanEditor";
import { useApp } from "../../context/AppContext";
import { isSignedAmountRecord, SignedAmountDialog } from "./SignedAmountDialog";
import { SubcontractSigningSummary } from "../SubcontractSigning";
import { SubcontractSigningLinkDialog } from "../SubcontractSigningLink";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const GROUPS = [["project", "项目", "Project"], ["pricing", "面积与价格", "Area & price"], ["sub", "分包商", "Subcontractor"], ["extra", "模板补充字段", "Template fields"]];
const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* 单个字段：显示「自动 · 来源」或「手动」，可恢复自动。 */
const FieldRow = ({ def, f, draft, onChange, zh }) => {
  const manual = draft !== undefined ? draft !== null : !f.auto;
  const val = draft !== undefined ? (draft ?? "") : (f.value ?? "");
  const Comp = def.type === "textarea" ? "textarea" : Input;
  return (
    <div className="space-y-1" data-testid={`dxf-field-${def.key}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-medium text-slate-600">{zh ? def.zh : def.en}</label>
        <span className="flex items-center gap-1">
          <span data-testid={`dxf-src-${def.key}`} className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${manual ? "bg-amber-100 text-amber-700" : f.source === "手填" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}>{manual ? (zh ? "手动" : "Manual") : f.source === "手填" ? (zh ? "手填" : "Enter") : `${zh ? "自动" : "Auto"} · ${f.source}`}</span>
          {manual && <button type="button" data-testid={`dxf-reset-${def.key}`} onClick={() => onChange(def.key, null)} title={zh ? "恢复自动" : "Reset to auto"} className="text-slate-400 hover:text-brand-600"><RotateCcw className="h-3 w-3" /></button>}
        </span>
      </div>
      <Comp data-testid={`dxf-input-${def.key}`} type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"} value={val} rows={def.type === "textarea" ? 3 : undefined}
        onChange={(e) => onChange(def.key, e.target.value)} placeholder={f.autoValue ? String(f.autoValue) : (zh ? "未拾取到，请填写" : "Not detected — enter")}
        className={`${def.type === "textarea" ? "w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400" : "h-8 text-xs"} ${manual ? "border-amber-300" : ""}`} />
    </div>
  );
};

/* 勾选组：单选（Included / Excluded…）或多选（Scope 清单）；「Other」勾中后可填写。
   2026-10-15 走查清单三-12（需求方回传备注「新添一个全选功能」）：
     背景：上一轮只做了「进页面时默认全勾」（见 backend/sub_docx_contract.py resolve 层），
           分包商改完勾选后想一键恢复到"全勾"只能逐个点，缺一个显式的全选入口。
     做法：给**多选组**加「全选 / 取消全选」按钮，一键把该组可选项全部勾上或全部清空。
     取舍：全选**只勾非 Other 的常规项**——Other 勾上会额外要求手填内容，
           一起勾会让用户措手不及；取消全选则清空整组（含 Other）。
     单选组不加：单选本身语义就是"二选一"，加全选没有意义。 */
const CheckGroup = ({ g, value, onChange, zh }) => {
  const on = value?.on || [];
  const toggle = (label) => {
    const has = on.includes(label);
    const next = g.multi ? (has ? on.filter((x) => x !== label) : [...on, label]) : (has ? [] : [label]);
    onChange(g.key, { on: next, other: value?.other || "" });
  };
  const otherOn = g.options.some((o) => o.other && on.includes(o.label));
  // 供「全选」使用的可勾选项（排除 Other），以及当前是否已全部勾上
  const selectable = g.options.filter((o) => !o.other).map((o) => o.label);
  const allOn = selectable.length > 0 && selectable.every((l) => on.includes(l));
  return (
    <div className="space-y-1" data-testid={`dxf-check-${g.key}`} data-multi={g.multi ? 1 : 0} data-count={on.length}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-medium text-slate-600">{g.label}</label>
        <span className="flex items-center gap-1.5">
          {g.multi && selectable.length > 0 && (
            <button type="button" data-testid={`dxf-check-${g.key}-all`} data-all={allOn ? 1 : 0}
              onClick={() => onChange(g.key, { on: allOn ? [] : selectable, other: value?.other || "" })}
              className="text-[10px] font-semibold text-brand-600 hover:text-brand-800 hover:underline">
              {allOn ? (zh ? "取消全选" : "Clear all") : (zh ? "全选" : "Select all")}
            </button>
          )}
          <span className="text-[9px] text-slate-400">{g.multi ? (zh ? "多选" : "Multi") : (zh ? "单选" : "One")}{on.length ? ` · ${on.length}` : ""}</span>
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {g.options.map((o, i) => {
          const active = on.includes(o.label);
          return (
            <button key={`${o.label}-${i}`} type="button" data-testid={`dxf-check-${g.key}-${i}`} data-on={active ? 1 : 0} onClick={() => toggle(o.label)}
              className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] transition-colors ${active ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-brand-300"}`}>
              {active ? <CheckSquare className="h-3 w-3" /> : <Square className="h-3 w-3" />}{o.label}
            </button>
          );
        })}
      </div>
      {otherOn && <Input data-testid={`dxf-check-${g.key}-other`} value={value?.other || ""} onChange={(e) => onChange(g.key, { on, other: e.target.value })} placeholder={zh ? "Other：请填写" : "Other: specify"} className="h-7 text-xs" />}
    </div>
  );
};

/* 勾选范围记忆横幅：带出同一分包商（或参考其他分包商）上次同工种合同的勾选 / 范围文字，待保存；可一键清空。 */
const MemoryBanner = ({ mem, zh, onClear }) => {
  const when = mem.savedAt ? String(mem.savedAt).slice(0, 10) : "";
  const ref = [mem.contractNo, mem.address, when].filter(Boolean).join(" · ");
  if (mem.cleared) return (
    <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2 flex items-start gap-1.5" data-testid="dxf-memory-banner" data-cleared="1" data-same-sub={mem.sameSub ? 1 : 0}>
      <History className="h-3.5 w-3.5 mt-0.5 shrink-0" />{zh ? "已清空带出的勾选与范围文字 · 点「保存」后此合同不再自动带出" : "Carried-over selections cleared · save to stop auto-fill for this contract"}
    </p>
  );
  return (
    <div className={`text-[11px] rounded-md px-2.5 py-2 flex items-start gap-1.5 border ${mem.sameSub ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-sky-50 border-sky-200 text-sky-800"}`} data-testid="dxf-memory-banner" data-cleared="0" data-same-sub={mem.sameSub ? 1 : 0}>
      <History className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span className="flex-1 min-w-0">
        {mem.sameSub ? (zh ? "已带出该分包商上次合同的勾选与范围文字" : "Carried over this subcontractor's last contract selections") : (zh ? `参考其他分包商（${mem.subName || "—"}）最近一份同工种合同的勾选与范围文字` : `Reference: latest same-trade contract by another subcontractor (${mem.subName || "—"})`)}
        {ref && <span className="block text-[10px] opacity-80 truncate" data-testid="dxf-memory-ref">{ref}</span>}
        <span className="block text-[10px] opacity-80">{zh ? "可微调后点「保存」写入本合同；不需要可一键清空。" : "Fine-tune and save to apply; or clear all."}</span>
      </span>
      <button type="button" onClick={onClear} className="shrink-0 text-[10px] font-semibold underline-offset-2 hover:underline" data-testid="dxf-memory-clear">{zh ? "一键清空" : "Clear all"}</button>
    </div>
  );
};

/* 「合同编辑」沿用 docx 字段编辑（自动拾取 + 手动覆盖 + 勾选项）与即时 PDF 预览，
   入口和标题统一改为业务名称，字段保存及签署锁定规则不变。 */
// 已签合同从登记金额入口直接进入金额更正，避免编辑正文后到保存才报锁定。
export const DocxFieldsDialog = (props) => {
  const [share, setShare] = useState(false);
  useEffect(() => { if (!props.open) setShare(false); }, [props.open]);
  // 编辑保存成功后直接进入链接面板，不保留可继续编辑的旧表单；重开时读取最新锁定状态。
  if (share && props.sc) return <SubcontractSigningLinkDialog api={props.api} sid={props.sc.id} zh={props.zh} open={props.open}
    autoGenerate onGenerated={() => props.onSaved?.()} onClose={() => { setShare(false); props.onClose(); }} />;
  // 固定版本签署期间直接给出进度入口，避免填写一屏字段后才在保存时报“已锁定”。
  if (props.sc?.signing?.status === "sub_pending") {
    return <SubcontractSigningLinkDialog api={props.api} sid={props.sc.id} zh={props.zh} open={props.open}
      onGenerated={() => props.onSaved?.()} onClose={props.onClose} />;
  }
  if (["preparing", "ceo_pending"].includes(props.sc?.signing?.status)) {
    return <Dialog open={props.open} onOpenChange={value => !value && props.onClose()}>
      <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{props.zh ? "合同签署中" : "Contract signing in progress"}</DialogTitle><DialogDescription>{props.zh ? "当前合同已固定。需要修改时，请先由经理撤回签署请求。" : "This version is locked. A manager must withdraw the signing request before editing."}</DialogDescription></DialogHeader><SubcontractSigningSummary sc={props.sc} zh={props.zh} /></DialogContent>
    </Dialog>;
  }
  return isSignedAmountRecord(props.sc) ? <SignedAmountDialog {...props} /> : <UnsignedDocxFieldsDialog {...props} onGenerate={() => setShare(true)} />;
};
const UnsignedDocxFieldsDialog = ({ open, onClose, api, zh, sc, onSaved, onGenerate }) => {
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [planDraft, setPlanDraft] = useState(null);   // 分笔支付编辑稿（null = 未改）
  const [maxed, setMaxed] = useState(false);
  const [draft, setDraft] = useState({});
  const [checks, setChecks] = useState({});
  const [mem, setMem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfKey, setPdfKey] = useState(0);
  const token = authStore.get("ali_token");
  const load = useCallback(() => {
    if (!sc) return;
    api.get(`/subcontracts/${sc.id}/docx-fields`).then((r) => {
      const m = r.data?.memory;
      setData(r.data); setDraft(m ? { ...(m.fields || {}) } : {}); setChecks(m ? { ...(m.checks || {}) } : {}); setMem(m ? { ...m.from, cleared: false } : null); setPlanDraft(null);
    }).catch((e) => toast.error(e?.response?.data?.detail || e.message));
  }, [api, sc]);
  useEffect(() => { if (open) { setData(null); setMem(null); load(); setPdfKey((k) => k + 1); } }, [open, load]);
  const onChange = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const onCheck = (k, v) => setChecks((c) => ({ ...c, [k]: v }));
  const clearAll = () => { setDraft({}); setChecks({}); setPlanDraft(null); setMem((m) => (m ? { ...m, cleared: true } : m)); };
  const sqft = (data?.computed?.pricing || "sqft") === "sqft";
  const preview = useMemo(() => {
    if (!data) return null;
    const g = (k) => { const d = draft[k]; const f = data.fields[k] || {}; return d !== undefined ? (d === null ? f.autoValue : d) : f.value; };
    if (!sqft) return { total: 0, amount: Number(g("amount") || 0) };
    const total = Number(g("areaAdu") || 0) + Number(g("areaExisting") || 0);
    return { total, amount: Math.round(Number(g("rate") || 0) * total * 100) / 100 };
  }, [data, draft, sqft]);
  const save = async () => {
    setBusy(true);
    try {
      const fields = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v === null ? "" : v]));
      const milestones = planDraft ? planDraft.map((r) => ({ id: r.id || undefined, name: r.name.trim(), percent: Number(r.percent) || 0, amount: Number(r.amount) || 0 })) : undefined;
      const { data: d } = await api.put(`/subcontracts/${sc.id}/docx-fields`, { fields, checks, milestones, memoryFrom: mem && !mem.cleared ? mem.contractNo || mem.id : undefined });
      setData((prev) => ({ ...prev, ...d })); setDraft({}); setChecks({}); setPlanDraft(null); setMem(null); setPdfKey((k) => k + 1);
      toast.success((zh ? `已保存 · 合同金额 ${money(d.contractAmount)}` : `Saved · ${money(d.contractAmount)}`) + (d.planRebuilt ? (zh ? " · 付款批次已按新模板重建" : " · payment plan rebuilt") : "")); onSaved?.(d);
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); }
  };
  const dirty = Object.keys(draft).length > 0 || Object.keys(checks).length > 0 || Boolean(mem?.cleared) || planDraft !== null;
  const planRows = planDraft || rowsFromComputed(data?.computed?.milestones);
  const planIsLocked = planLocked(data?.computed?.milestones);
  const planOk = !planDraft || (preview && (preview.amount <= 0 || Math.abs(planDraft.reduce((a, r) => a + Number(r.amount || 0), 0) - preview.amount) <= Math.max(1, preview.amount * 0.005))) && planDraft.every((r) => r.name.trim());
  const saveTemplate = async (rows) => {
    setBusy(true);
    try {
      await api.put(`/subcontract-trades/${sc.tradeKey}/plan`, { milestones: templateRows(rows) });
      toast.success(zh ? `已存为「${sc.tradeName}」工种模板 · 新建该工种合同将按此生成` : `Saved as ${sc.tradeName} template`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const applyTemplate = async () => {
    setBusy(true);
    try {
      await api.put(`/subcontracts/${sc.id}/payment-plan`, { reset: true });
      toast.success(zh ? "已套用工种模板（可继续修改）" : "Trade template applied"); setPlanDraft(null); load(); setPdfKey((k) => k + 1); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const c = data?.computed || {};
  const checkDefs = data?.checks || [];
  const checkVal = (k) => (checks[k] !== undefined ? checks[k] : (data?.checkValues || {})[k]);
  const pdfUrl = sc ? `${BACKEND}/api/subcontracts/${sc.id}/contract-preview.pdf?signed=0&auth=${token}&v=${pdfKey}` : "";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className={`${maxed ? "max-w-[100vw] w-[100vw] h-[100dvh] rounded-none" : "max-w-[96vw] w-[1200px] h-[92dvh]"} p-0 gap-0 overflow-hidden grid-rows-[auto_1fr] z-[95]`} overlayClassName="z-[94]" data-testid="dxf-dialog" data-maxed={maxed ? 1 : 0} data-pricing={c.pricing || ""} onEscapeKeyDown={(e) => { e.stopPropagation(); onClose(); }}>
        <DialogHeader className="px-4 py-3 border-b border-slate-100">
          <DialogDescription className="sr-only">Contract template fields with auto-detected values and live PDF preview</DialogDescription>
          <DialogTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
            <FileSignature className="h-4 w-4 text-brand-500" />{zh ? "合同编辑 · " : "Edit contract · "}{sc?.tradeName}
            {c.contractNo && <span className="font-mono text-[11px] text-slate-500" data-testid="dxf-contract-no">{c.contractNo}</span>}
            {c.templateName && <span className="text-[10px] text-slate-400 font-normal" data-testid="dxf-template-name">· {c.templateName}</span>}
            <button type="button" data-testid="dxf-maximize" onClick={() => setMaxed((v) => !v)} className="ml-auto mr-7 inline-flex items-center gap-1 text-[11px] font-normal text-slate-500 hover:text-slate-900 rounded-md border border-slate-200 px-2 py-1" title={maxed ? (zh ? "还原窗口" : "Restore") : (zh ? "最大化" : "Maximize")}>
              {maxed ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}{maxed ? (zh ? "还原" : "Restore") : (zh ? "最大化" : "Maximize")}
            </button>
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="flex items-center justify-center text-slate-400 text-xs gap-2"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "读取字段…" : "Loading…"}</div> : (
          <div className={`grid ${maxed ? "md:grid-cols-[480px_1fr]" : "md:grid-cols-[420px_1fr]"} min-h-0 h-full`}>
            <div className="grid grid-rows-[1fr_auto] min-h-0 border-r border-slate-100">
            <div className="overflow-y-auto p-4 space-y-4 text-xs min-h-0" data-testid="dxf-form">
              <p className="text-[11px] text-slate-500 bg-slate-50 rounded-md px-2.5 py-2 flex items-start gap-1.5"><Wand2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
                {sqft ? (zh ? "绿色 = 系统自动拾取（项目地址 / 业主合同面积 / 工种定价 / 分包商资料）；改动后变为手动，可随时恢复自动。合同金额 = 单价 × 总面积。" : "Green = auto-detected (address / owner-contract areas / trade pricing / sub profile). Edited fields become manual; reset anytime. Amount = rate × total area.")
                  : (zh ? "绿色 = 系统自动拾取（项目地址 / 合同金额 / 分包商资料）；灰色 = 需手填；勾选项按合同原文 ☐ 生成，勾中后 PDF 显示 ☒。保存后未发起的付款批次按模板比例重算。" : "Green = auto-detected (address / contract amount / sub profile); grey = enter manually; checkboxes mirror the contract text and render as ☒. Planned milestones re-scale on save.")}
              </p>
              {mem && <MemoryBanner mem={mem} zh={zh} onClear={clearAll} />}
              {GROUPS.filter(([g]) => data.defs.some((d) => d.group === g)).map(([g, gz, ge]) => (
                <section key={g} className="space-y-2.5" data-testid={`dxf-group-${g}`}>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{zh ? gz : ge}</h4>
                  {data.defs.filter((d) => d.group === g).map((d) => <FieldRow key={d.key} def={d} f={data.fields[d.key]} draft={draft[d.key]} onChange={onChange} zh={zh} />)}
                  {g === "pricing" && preview && (
                    <>
                      <div className="rounded-md bg-slate-900 text-slate-100 px-3 py-2 space-y-0.5" data-testid="dxf-computed">
                        {sqft && <p className="flex justify-between"><span className="text-slate-400">{zh ? "总面积" : "Total area"}</span><b data-testid="dxf-total-area">{preview.total.toLocaleString()} sq.ft.</b></p>}
                        <p className="flex justify-between"><span className="text-slate-400">{sqft ? (zh ? "合同金额（自动）" : "Contract amount (auto)") : (zh ? "合同总价" : "Contract price")}</span><b data-testid="dxf-amount" className="text-emerald-300">{money(preview.amount)}</b></p>
                        {dirty && !planDraft && <p className="text-[10px] text-amber-300">{zh ? "保存后生效并按比例重算未发起的付款批次" : "Applies on save; planned milestones re-scaled"}</p>}
                      </div>
                      <DocxPlanEditor rows={planRows} total={preview.amount} onChange={setPlanDraft} locked={planIsLocked} zh={zh} user={user} tradeName={sc?.tradeName} onSaveTemplate={saveTemplate} onApplyTemplate={applyTemplate} saving={busy} />
                    </>
                  )}
                </section>
              ))}
              {checkDefs.length > 0 && (
                <section className="space-y-2.5" data-testid="dxf-group-checks" data-count={checkDefs.length}>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{zh ? "合同勾选项（☐ → ☒）" : "Contract selections (☐ → ☒)"}</h4>
                  {checkDefs.map((g) => <CheckGroup key={g.key} g={g} value={checkVal(g.key)} onChange={onCheck} zh={zh} />)}
                </section>
              )}
              <section className="space-y-1.5 text-[11px] text-slate-500 border-t border-slate-100 pt-3" data-testid="dxf-meta">
                <p className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-brand-500" />{zh ? "验证码" : "Verify code"} <span className="font-mono text-slate-700" data-testid="dxf-verify-code">{c.verifyCode}</span></p>
                <p>{zh ? "内容指纹" : "Fingerprint"} <span className="font-mono text-slate-700" data-testid="dxf-fingerprint">{c.fingerprint}</span> · {zh ? "验证页" : "Verify page"} <a href={c.verifyUrl} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline" data-testid="dxf-verify-link">{String(c.verifyUrl || "").replace(/^https?:\/\//, "")}</a></p>
                <p data-testid="dxf-terms">{zh ? "付款条款" : "Payment terms"} · {c.paymentTerms}</p>
              </section>
            </div>
            <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-t border-slate-100 bg-white">
              {/* 保存失败绝不冻结旧 PDF；没有编辑也可为当前已保存合同生成签署链接。 */}
              <Button onClick={async () => { if (!busy && planOk && await save()) onGenerate(); }} disabled={busy || !planOk} className="w-full bg-brand-500 hover:bg-brand-600 min-h-[44px] text-xs" data-testid="dxf-save-sign-link"><FileSignature className="h-4 w-4 mr-1.5" />{zh ? "保存并生成签署链接" : "Save & create signing link"}</Button>
              <Button onClick={save} disabled={!dirty || busy || !planOk} className="bg-brand-500 hover:bg-brand-600 h-8 text-xs" data-testid="dxf-save">{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存并刷新预览" : "Save & refresh preview"}</Button>
              <Button variant="outline" onClick={() => setPdfKey((k) => k + 1)} className="h-8 text-xs" data-testid="dxf-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />{zh ? "刷新预览" : "Refresh"}</Button>
              {dirty && <button type="button" onClick={clearAll} className="text-[11px] text-slate-400 hover:text-slate-700 ml-auto" data-testid="dxf-discard">{zh ? "放弃改动" : "Discard"}</button>}
            </div>
            </div>
            <div className="min-h-0 hidden md:grid grid-rows-[auto_1fr] bg-slate-800">
              <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-slate-300 border-b border-white/10">
                <span>{zh ? "合同 PDF 预览（保存后自动刷新）" : "Contract PDF preview (refreshes on save)"}</span>
                <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white" data-testid="dxf-open-pdf"><ExternalLink className="h-3.5 w-3.5" />{zh ? "新标签页打开" : "Open in new tab"}</a>
              </div>
              <PdfPane api={api} sc={sc} pdfKey={pdfKey} zh={zh} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* 预览经 axios 取 blob：生成失败时显示后端原因 + 重试，而不是 iframe 里的裸 "Internal Server Error"。 */
const PdfPane = ({ api, sc, pdfKey, zh }) => {
  const [state, setState] = useState({ url: "", err: "", loading: true });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!sc) return undefined;
    let url = "";
    setState({ url: "", err: "", loading: true });
    api.get(`/subcontracts/${sc.id}/contract-preview.pdf?signed=0&v=${pdfKey}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setState({ url, err: "", loading: false }); })
      .catch(async (e) => {
        let msg = e?.message || "";
        try { const t = await e?.response?.data?.text?.(); msg = JSON.parse(t)?.detail || t || msg; } catch { /* keep */ }
        setState({ url: "", err: `${e?.response?.status || ""} ${msg}`.trim(), loading: false });
      });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [api, sc, pdfKey, retry]);
  if (state.loading) return <div className="flex items-center justify-center text-slate-300 text-xs gap-2" data-testid="dxf-preview-loading"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "生成合同 PDF…（LibreOffice 转换约 3–5 秒）" : "Generating PDF… (3–5 s)"}</div>;
  if (state.err) return (
    <div className="flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="dxf-preview-error">
      <p className="text-sm font-semibold text-red-300">{zh ? "合同 PDF 生成失败" : "Contract PDF generation failed"}</p>
      <p className="text-[11px] text-slate-300 break-all max-w-md" data-testid="dxf-preview-error-detail">{state.err}</p>
      <Button variant="outline" onClick={() => setRetry((k) => k + 1)} className="h-8 text-xs" data-testid="dxf-preview-retry"><RefreshCw className="h-3.5 w-3.5 mr-1" />{zh ? "重试" : "Retry"}</Button>
    </div>
  );
  return <iframe title="contract" data-testid="dxf-preview-frame" className="w-full h-full" src={`${state.url}#navpanes=0&view=FitH`} />;   // navpanes=0：不显示左侧缩略图栏
};
